import { cart } from '../helpers/cart'
import { globalState } from '../helpers/utils'

/**
 * BulkProductForm - Custom HTML Form Element for bulk variant purchases
 *
 * Mimics EnhancedProductForm, but allows users to select quantities for multiple variants and add them all to the cart at once.
 */
export class BulkProductForm extends HTMLElement {
  private form: HTMLFormElement
  // Form configuration
  private config = {
    productId: '',
    productTitle: ''
  }

  // UI elements
  private uiElements = {
    wrapper: null as HTMLElement | HTMLFormElement | Element | null,
    success: null as HTMLElement | null,
    error: null as HTMLElement | null
  }

  // CSS classes and selectors
  private ui = {
    selectors: {
      showOnSuccess: '[data-show-on-success]',
      hideOnSuccess: '[data-hide-on-success]',
      errorMessage: '[data-error-message]',
      successMessage: '[data-success-message]',
      uiAttribute: 'data-ui',
      quantityMinus: '[data-quantity-minus]',
      quantityPlus: '[data-quantity-plus]',
      quantityInput: '[data-quantity-input]',
      quantityContainer: '[data-quantity-container]',
      variantTotal: '[data-variant-total]'
    },
    classes: {
      visible: 'visible',
      errorField: 'form-error',
      errorClass: 'form-error w-full paragraph-s font-bold text-system-error bg-system-error-light px-1 py-0.5 mt-0.25'
    }
  }

  constructor() {
    super()
    // Treat the custom element itself as the form container
    this.form = this as unknown as HTMLFormElement
    this.initConfig()
    this.initUIElements()
    this.addEventListeners()
    // Ensure only 'Select Sizes' is shown on load
    setTimeout(() => this.updateTotalCost(), 0)
  }

  /**
   * Initialize configuration from data attributes or properties
   */
  private initConfig() {
    this.config = {
      productId: this.dataset.productId || '',
      productTitle: this.dataset.productTitle || ''
    }
  }

  /**
   * Initialize UI element references
   */
  private initUIElements() {
    const wrapper = this.closest('.product-form') || this
    this.uiElements = {
      wrapper,
      success: wrapper.querySelector(this.ui.selectors.successMessage),
      error: wrapper.querySelector(this.ui.selectors.errorMessage)
    }
  }

  /**
   * Get the appropriate price for a variant based on B2B status and quantity
   */
  private getVariantPrice(row: HTMLElement): number {
    const isB2B = globalState?.customer?.b2b || false
    const variantQuantity = this.getVariantQuantity(row)

    console.log('getVariantPrice - B2B:', isB2B, 'Variant quantity:', variantQuantity)

    if (isB2B) {
      // Check if B2B pricing is available for this specific variant
      const b2bPrice = row.getAttribute('data-variant-b2b-price')
      const quantityBreaksJson = row.getAttribute('data-variant-quantity-breaks')

      console.log('B2B data - b2bPrice:', b2bPrice, 'quantityBreaksJson:', quantityBreaksJson)

      if (b2bPrice && quantityBreaksJson) {
        try {
          // Decode HTML entities that Liquid might have escaped
          const decodedJson = this.decodeHtmlEntities(quantityBreaksJson)
          console.log('Decoded JSON:', decodedJson)
          const quantityBreaks = JSON.parse(decodedJson) as Array<{
            minimum_quantity: number;
            price: number;
          }>

          console.log('Parsed quantity breaks:', quantityBreaks)

          if (quantityBreaks && Array.isArray(quantityBreaks) && quantityBreaks.length > 0) {
            // Validate and find the appropriate price break for this variant's quantity
            // Only apply B2B pricing if this variant's quantity meets minimum threshold
            for (const break_ of quantityBreaks) {
              console.log('Break object:', break_)
              console.log('Break keys:', Object.keys(break_))

              // Validate the price break data to prevent NaN values
              if (typeof break_.minimum_quantity !== 'number' ||
                  isNaN(break_.minimum_quantity) ||
                  break_.minimum_quantity <= 0 ||
                  typeof break_.price !== 'number' ||
                  isNaN(break_.price) ||
                  break_.price <= 0) {
                console.warn('Invalid price break data:', break_)
                continue
              }

              console.log('Checking break:', break_.minimum_quantity, 'vs variant quantity:', variantQuantity)
              if (variantQuantity >= break_.minimum_quantity) {
                console.log('Applying B2B price:', break_.price)
                return break_.price
              }
            }

            console.log('No B2B break met, using regular price')
          }
          // If this variant's quantity doesn't meet any break threshold, use regular price
          return parseInt(row.getAttribute('data-variant-price') || '0', 10)
        } catch (error) {
          console.error('Error parsing quantity price breaks:', error)
          console.error('Raw JSON string:', quantityBreaksJson)
        }
      }
    }

    const regularPrice = parseInt(row.getAttribute('data-variant-price') || '0', 10)
    console.log('Using regular price:', regularPrice)
    // Fallback to regular price
    return regularPrice
  }

  /**
   * Get the quantity for a specific variant
   */
  private getVariantQuantity(row: HTMLElement): number {
    const variantId = row.getAttribute('data-variant-id')
    const input = this.form.querySelector(`input[data-variant-id="${variantId}"]`) as HTMLInputElement
    return input ? (parseInt(input.value, 10) || 0) : 0
  }

  /**
   * Get the total quantity across all variants for this product
   */
  private getTotalQuantityForProduct(): number {
    const inputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(this.ui.selectors.quantityInput))
    return inputs.reduce((total, input) => {
      return total + (parseInt(input.value, 10) || 0)
    }, 0)
  }

  /**
   * Decode HTML entities in a string
   */
  private decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    return textarea.value
  }

  /**
   * Update the total cost display in the Add to Cart button
   */
  private updateTotalCost() {
    let total = 0
    let totalQty = 0
    const inputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(this.ui.selectors.quantityInput))
    inputs.forEach(input => {
      const qty = parseInt(input.value, 10) || 0
      totalQty += qty
      // Find the parent with data-variant
      const row = input.closest('[data-variant]') as HTMLElement | null
      if (row) {
        const price = this.getVariantPrice(row)
        total += qty * price
        // Per-variant modal visibility
        const rowId = row.getAttribute('id')
        if (rowId) {
          const modal = document.querySelector(`x-modal[data-id="${rowId}"]`)
          if (modal) {
            if (qty > 0) {
              modal.setAttribute('data-ui', 'visible')
            } else {
              modal.removeAttribute('data-ui')
            }
          }
        }
      }
    })
    // Update total cost in button
    const btn = this.form.querySelector('button[type="submit"]')
    if (btn) {
      let totalSpan = btn.querySelector('.bulk-total-cost') as HTMLSpanElement
      if (!totalSpan) {
        totalSpan = document.createElement('span')
        totalSpan.className = 'bulk-total-cost ml-1 font-bold'
        btn.appendChild(totalSpan)
      }
      totalSpan.textContent = total > 0 ? `(${this.formatMoney(total)})` : ''
    }
    // Show/hide modal or parent with data-id matching form id (for the whole form)
    const formId = this.getAttribute('id')
    if (formId) {
      const modal = document.querySelector(`[data-id="${formId}"]`)
      if (modal) {
        if (totalQty > 0) {
          modal.setAttribute('data-ui', 'visible')
        } else {
          modal.removeAttribute('data-ui')
        }
      }
    }
  }

  /**
   * Add event listeners for form actions and quantity controls
   */
  private addEventListeners() {
    this.form.addEventListener('click', this.handleQuantityButton.bind(this))
    this.form.addEventListener('input', this.handleQuantityInput.bind(this))

    // Listen for clicks on the submit button since this is not a form element
    this.form.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      if (target.closest('button[type="submit"]')) {
        event.preventDefault()
        this.handleSubmit(event as any)
      }
    })
  }

  /**
   * Handle increment/decrement buttons
   */
  private handleQuantityButton(event: Event) {
    const target = event.target as HTMLElement
    if (target.closest(this.ui.selectors.quantityPlus) || target.closest(this.ui.selectors.quantityMinus)) {
      const isPlus = !!target.closest(this.ui.selectors.quantityPlus)
      const isMinus = !!target.closest(this.ui.selectors.quantityMinus)
      // Find the quantity container that holds both the button and input
      const container = target.closest(this.ui.selectors.quantityContainer)
      const input = container?.querySelector(this.ui.selectors.quantityInput) as HTMLInputElement
      if (!input) return
      let value = parseInt(input.value, 10) || 0
      const min = parseInt(input.min, 10) || 0
      const max = parseInt(input.max, 10) || 999
      if (isPlus) {
        if (value < max) value++
      } else if (isMinus) {
        if (value > min) value--
      }
      input.value = value.toString()
      input.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  /**
   * Handle quantity input changes and update row total and total cost
   */
  private handleQuantityInput(event: Event) {
    const input = event.target as HTMLInputElement
    if (!input.matches(this.ui.selectors.quantityInput)) return

    // Recalculate all variant prices when any quantity changes
    this.recalculateAllVariantPrices()

    // Update total cost
    this.updateTotalCost()
  }

  /**
   * Recalculate prices for all variants based on current total quantity
   */
  private recalculateAllVariantPrices() {
    const inputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(this.ui.selectors.quantityInput))
    const totalQuantity = this.getTotalQuantityForProduct()

    console.log('Recalculating prices. Total quantity:', totalQuantity)

    inputs.forEach(input => {
      const row = input.closest('[data-variant]') as HTMLElement | null
      if (row) {
        const priceCell = row.querySelector(this.ui.selectors.variantTotal) as HTMLElement
        const qty = parseInt(input.value, 10) || 0
        const price = this.getVariantPrice(row)

        console.log('Variant:', row.getAttribute('data-variant-id'), 'Qty:', qty, 'Price:', price, 'Total:', qty * price)

        if (priceCell) {
          const formattedPrice = this.formatMoney(qty * price)
          priceCell.textContent = formattedPrice
          console.log('Updated price cell:', priceCell.textContent)
        } else {
          console.error('Price cell not found for variant:', row.getAttribute('data-variant-id'))
        }
      }
    })
  }

  /**
   * Format cents to money string (assumes input is cents)
   */
  private formatMoney(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`
  }

  /**
   * Handle form submission for bulk add
   */
  private async handleSubmit(event: SubmitEvent) {
    event.preventDefault()
    console.log('Form submission started')

    // Disable the submit button to prevent double-clicking
    const submitButton = this.form.querySelector('button[type="submit"]') as HTMLButtonElement
    let originalButtonContent = ''
    if (submitButton) {
      originalButtonContent = submitButton.innerHTML
      submitButton.disabled = true
      submitButton.textContent = 'Adding to Bag...'
    }

    // Remove previous error messages
    this.form.querySelectorAll('.form-error').forEach(el => el.remove())
    let hasError = false

    // Collect all quantity inputs with value > 0
    const inputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(this.ui.selectors.quantityInput))
    console.log('Found inputs:', inputs.length)

    const items = inputs
      .map(input => {
        const quantity = parseInt(input.value, 10) || 0
        const max = parseInt(input.max, 10)
        // Find the parent with data-variant
        const row = input.closest('[data-variant]') as HTMLElement | null
        const id = row ? row.getAttribute('data-variant-id') : null

        console.log('Processing input - quantity:', quantity, 'max:', max, 'id:', id)

        // Validation: quantity must not exceed max
        if (quantity > max) {
          hasError = true
          console.log('Quantity exceeds max for variant:', id)
          // Show error message directly under the [data-variant] element
          if (row && !row.querySelector('.form-error')) {
            const errorDiv = document.createElement('div')
            errorDiv.className = this.ui.classes.errorClass
            row.appendChild(errorDiv)
          }
        }
        return id && quantity > 0 ? { id: Number(id), quantity, row } : null
      })
      .filter(Boolean) as { id: number, quantity: number, row: HTMLElement }[]

    console.log('Items to add:', items)

    if (hasError) {
      console.log('Form has errors, stopping submission')
      // Re-enable the submit button
      if (submitButton) {
        submitButton.disabled = false
        submitButton.innerHTML = originalButtonContent
      }
      return
    }
    if (items.length === 0) {
      console.log('No items to add')
      // Re-enable the submit button
      if (submitButton) {
        submitButton.disabled = false
        submitButton.innerHTML = originalButtonContent
      }
      // Optionally show a UI message
      return
    }

    try {
      console.log('Calling cart.addLineItems with:', items.map(({ id, quantity }) => ({ id, quantity })))
      const response = await cart.addLineItems(items.map(({ id, quantity }) => ({ id, quantity })))
      console.log('Cart response:', response)

      if (response && response.error && items.length > 0) {
        console.log('Cart error:', response.error)
        const row = items[0].row
        if (row && !row.querySelector('.form-error')) {
          const errorDiv = document.createElement('div')
          errorDiv.className = this.ui.classes.errorClass
          errorDiv.textContent = response.error
          row.appendChild(errorDiv)
        }
      }
      // Optionally reset form or show a UI message
    } catch (error) {
      console.error('Error adding items to cart:', error)
      // Optionally show a UI error message
    } finally {
      // Re-enable the submit button
      if (submitButton) {
        submitButton.disabled = false
        submitButton.innerHTML = originalButtonContent
      }
    }
  }
}
